import { Button } from '@/common/ui/button'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogHeader,
	DialogTitle,
} from '@/common/ui/dialog'
import { useState } from 'react'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationToggleDemoAccount from '@/modules/manage-player/api/hooks/useMutationToggleDemoAccount'
import { useQueryClient } from '@tanstack/react-query'
import { PlayerQueryKeys } from '@/modules/manage-player/keys'

export type PlayerToggleDemoPlayer = {
	id: string
	phone: string
	isAgentDemoAccount?: boolean
}

interface PlayerToggleDemoModalProps {
	player: PlayerToggleDemoPlayer
}

export default function PlayerToggleDemoModal({
	player,
}: PlayerToggleDemoModalProps) {
	const [open, setOpen] = useState(false)
	const { mutate: toggleDemo } = useMutationToggleDemoAccount()
	const queryClient = useQueryClient()

	const handleToggle = () => {
		try {
			toggleDemo(
				{ playerId: player.id },
				{
					onSuccess: (data) => {
						const msg = data.isAgentDemoAccount
							? 'เปิดบัญชีทดลองเรียบร้อยแล้ว'
							: 'ปิดบัญชีทดลองเรียบร้อยแล้ว'
						toast.success(msg)
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerList],
						})
						queryClient.invalidateQueries({
							queryKey: [PlayerQueryKeys.GetPlayerDetail, player.id],
						})
						setOpen(false)
					},
					onError: (error) => {
						showErrorToast(error as Error)
					},
				},
			)
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const isDemo = player.isAgentDemoAccount ?? false

	return (
		<>
			<Button
				type="button"
				variant={isDemo ? 'destructive' : 'outline'}
				size="sm"
				className="h-8 shrink-0"
				onClick={() => setOpen(true)}
			>
				{isDemo ? 'ปิดบัญชีทดลอง' : 'เปิดบัญชีทดลอง'}
			</Button>

			<Dialog open={open} onOpenChange={setOpen}>
				<DialogContent className="sm:max-w-[425px]">
					<DialogHeader>
						<DialogTitle>
							{isDemo
								? `ยืนยันปิดบัญชีทดลอง (${player.phone})`
								: `ยืนยันเปิดบัญชีทดลอง (${player.phone})`}
						</DialogTitle>
						<DialogDescription asChild>
							<div className="space-y-3 pt-2 text-left text-sm text-foreground">
								<p>
									{isDemo
										? 'คุณต้องการปิดสถานะบัญชีทดลองของผู้เล่นนี้ใช่หรือไม่?'
										: 'คุณต้องการเปิดสถานะบัญชีทดลองของผู้เล่นนี้ใช่หรือไม่?'}
								</p>
								<p className="text-muted-foreground">
									{isDemo
										? 'เมื่อปิดสถานะบัญชีทดลอง บัญชีนี้จะถูกนับรวมในรายได้ตามปกติ'
										: 'บัญชีนี้จะไม่ถูกนับรวมในรายได้'}
								</p>
							</div>
						</DialogDescription>
					</DialogHeader>

					<div className="flex justify-end gap-4 pt-4">
						<Button
							variant="outline"
							onClick={() => setOpen(false)}
							type="button"
						>
							ยกเลิก
						</Button>
						<Button
							onClick={handleToggle}
							variant={isDemo ? 'destructive' : 'default'}
						>
							{isDemo ? 'ปิดบัญชีทดลอง' : 'เปิดบัญชีทดลอง'}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</>
	)
}
